// Plain TS module (this project has no Composition API) resolving OS/locale context
// injected by a native WebView shell, a URL query param, or falling back to the browser.
//
// Native side (recommended) injects one of these before the bundle loads:
//   window.__APP_CONTEXT__ = { os: 'ios', locale: 'de-DE', appVersion: '3.2.1' }
// or appends query params:  ?os=ios&locale=de-DE

// "ru" temporarily excluded: Plus Jakarta Sans has no base Cyrillic glyph coverage (only cyrillic-ext),
// so Russian text falls back to the browser's default sans-serif and renders inconsistently bold.
export const SUPPORTED_LANGS = ["en", "de", "es"];
const DEFAULT_LANG = "en";

interface AppContext {
    os?: string;
    locale?: string;
    appVersion?: string;
}

interface ResolvedContext {
    os: string;
    isIOS: boolean;
    isAndroid: boolean;
    locale: string;
    lang: string;
    source: string;
}

declare global {
    interface Window {
        __APP_CONTEXT__?: AppContext;
    }
}

function fromNative(): { os: string | null; locale: string | null; source: string } | null {
    const ctx = window.__APP_CONTEXT__;
    if (ctx && typeof ctx === "object") {
        return { os: ctx.os || null, locale: ctx.locale || null, source: "native" };
    }
    return null;
}

function fromQuery(): { os: string | null; locale: string | null; source: string } | null {
    const params = new URLSearchParams(window.location.search);
    const os = params.get("os");
    const locale = params.get("locale");
    if (os || locale) return { os: os || null, locale: locale || null, source: "query" };
    return null;
}

function sniffOS(): string {
    const ua = navigator.userAgent || "";
    if (/Android/i.test(ua)) return "android";
    const iOSLike =
        /iPhone|iPad|iPod/i.test(ua) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1); // iPadOS
    if (iOSLike) return "ios";
    return "unknown";
}

function sniffLocale(): string {
    return navigator.language || (navigator.languages && navigator.languages[0]) || DEFAULT_LANG;
}

function normalizeLang(locale: string | null): string {
    if (!locale) return DEFAULT_LANG;
    const short = String(locale).toLowerCase().split("-")[0];
    return SUPPORTED_LANGS.includes(short) ? short : DEFAULT_LANG;
}

export function resolveDeviceContext(): ResolvedContext {
    const injected = fromNative() || fromQuery() || { os: null, locale: null, source: "sniff" };
    const os = (injected.os || sniffOS()).toLowerCase();
    const rawLocale = injected.locale || sniffLocale();

    return {
        os,
        isIOS: os === "ios",
        isAndroid: os === "android",
        locale: rawLocale,
        lang: normalizeLang(rawLocale),
        source: injected.source
    };
}
